/**
 * Date helpers that operate on ISO local dates (YYYY-MM-DD) without pulling in a library.
 * Everything here is timezone-agnostic: callers pass local dates already resolved for the
 * athlete's timezone (see `todayInTimezone`).
 */

const DAY_MS = 86_400_000

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

export function assertIsoDate(value: unknown, label = 'date'): string {
  if (!isIsoDate(value)) throw new Error(`${label} must be YYYY-MM-DD, got ${String(value)}`)
  return value
}

/** Parse YYYY-MM-DD into a UTC Date at midnight. */
export function parseIsoDate(date: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d))
}

/** Format a Date (UTC components) as YYYY-MM-DD. */
export function formatIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Take the date part of an ISO datetime such as `2026-09-05T06:12:00`. */
export function dateOf(datetimeLocal: string): string {
  return datetimeLocal.slice(0, 10)
}

export function addDays(date: string, days: number): string {
  return formatIsoDate(new Date(parseIsoDate(date).getTime() + days * DAY_MS))
}

/** Whole days from `a` to `b` (positive when b is later). */
export function daysBetween(a: string, b: string): number {
  return Math.round((parseIsoDate(b).getTime() - parseIsoDate(a).getTime()) / DAY_MS)
}

/** Inclusive list of dates from `from` to `to`. */
export function eachDay(from: string, to: string): string[] {
  const n = daysBetween(from, to)
  if (n < 0) return []
  const out: string[] = []
  for (let i = 0; i <= n; i++) out.push(addDays(from, i))
  return out
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(date: string): number {
  return (parseIsoDate(date).getUTCDay() + 6) % 7
}

/** Monday of the ISO week containing `date`. */
export function weekStart(date: string): string {
  return addDays(date, -weekdayIndex(date))
}

/** Sunday of the ISO week containing `date`. */
export function weekEnd(date: string): string {
  return addDays(weekStart(date), 6)
}

/** ISO 8601 week label, e.g. 2026-W36. */
export function isoWeekLabel(date: string): string {
  const d = parseIsoDate(date)
  // Thursday of this week decides the ISO year.
  const thursday = new Date(d.getTime() + (3 - ((d.getUTCDay() + 6) % 7)) * DAY_MS)
  const isoYear = thursday.getUTCFullYear()
  const jan4 = new Date(Date.UTC(isoYear, 0, 4))
  const week1Monday = new Date(jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * DAY_MS)
  const week = Math.round((weekStartDate(d).getTime() - week1Monday.getTime()) / (7 * DAY_MS)) + 1
  return `${isoYear}-W${String(week).padStart(2, '0')}`
}

function weekStartDate(d: Date): Date {
  return new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY_MS)
}

/** Mondays covering [from, to]. */
export function eachWeekStart(from: string, to: string): string[] {
  const out: string[] = []
  let cursor = weekStart(from)
  const last = weekStart(to)
  while (cursor <= last) {
    out.push(cursor)
    cursor = addDays(cursor, 7)
  }
  return out
}

export function monthStart(date: string): string {
  return `${date.slice(0, 7)}-01`
}

/** Today's local date in an IANA timezone, e.g. "America/Chicago". */
export function todayInTimezone(timezone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}`
}

export function clampRange(from: string, to: string): { from: string; to: string } {
  return from <= to ? { from, to } : { from: to, to: from }
}

/**
 * Human formatting helpers shared by UI and tests. Durations are h:mm:ss from an hour up and
 * m:ss under it, so "1:15" always means a minute and a quarter, never an hour and a quarter.
 */
export function formatDuration(secs: number | null | undefined): string {
  if (secs == null || !Number.isFinite(secs)) return '–'
  const s = Math.round(secs)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = String(s % 60).padStart(2, '0')
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${sec}`
  return `${m}:${sec}`
}

export function formatHours(secs: number | null | undefined, digits = 1): string {
  if (secs == null || !Number.isFinite(secs)) return '–'
  // Round from whole seconds: 27,180 s is 7.55 h, which toFixed alone shows as 7.5.
  const scale = 10 ** digits
  return `${(Math.round((secs * scale) / 3600) / scale).toFixed(digits)} h`
}

/**
 * An amount of time in words, where a clock reading would mislead (a night's sleep, a week's
 * training, a gap): "45 min", "2 h", "6 h 23 min". Always "min", never "m", which reads as metres
 * beside a swim. The sign is the caller's: a negative span formats as its size.
 */
export function formatSpan(secs: number | null | undefined): string {
  if (secs == null || !Number.isFinite(secs)) return '–'
  const total = Math.round(Math.abs(secs) / 60)
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} min`
  return m === 0 ? `${h} h` : `${h} h ${m} min`
}

/**
 * A gap between two times, in words to the second, never a clock that reads like a time of its
 * own: "3 s", "1 min 15 s", "12 min". Past an hour it reads like any amount of time ("1 h 5 min").
 */
export function formatGap(secs: number | null | undefined): string {
  if (secs == null || !Number.isFinite(secs)) return '–'
  const s = Math.round(Math.abs(secs))
  if (s < 60) return `${s} s`
  if (s >= 3600) return formatSpan(s)
  const m = Math.floor(s / 60)
  const r = s % 60
  return r === 0 ? `${m} min` : `${m} min ${r} s`
}

/** Pace in s/km → "5:12 /km". */
export function formatPace(secPerKm: number | null | undefined, unit: 'km' | 'mi' = 'km'): string {
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm <= 0) return '–'
  const s = Math.round(unit === 'mi' ? secPerKm * 1.609344 : secPerKm)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${String(sec).padStart(2, '0')} /${unit}`
}

/** Swim pace: s/100m. */
export function formatSwimPace(secPer100m: number | null | undefined): string {
  if (secPer100m == null || !Number.isFinite(secPer100m) || secPer100m <= 0) return '–'
  const s = Math.round(secPer100m)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${String(sec).padStart(2, '0')} /100m`
}

export function formatDistance(
  metres: number | null | undefined,
  unit: 'km' | 'mi' = 'km',
): string {
  if (metres == null || !Number.isFinite(metres)) return '–'
  const v = unit === 'mi' ? metres / 1609.344 : metres / 1000
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${unit}`
}

/**
 * The sync worker backfills history back to this date, so older Garmin exports uploaded to
 * intervals.icu come in too. A walk that reached it stores this date (or the day before) as
 * the athlete's history start.
 */
export const HISTORY_START = '2010-01-01'
